import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useState } from 'react';
import ActionOnCommandIcon from '../../../svg-icons/ActionOnCommandIcon';
import OrderCancelIcon from '../../../svg-icons/OrderCancelIcon';
import RefundRecap from './RefundRecap.jsx';

const OrderRetractRecap = ({ retraction, currency }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltip, setTooltip] = useState();

  const handleCopy = (value) => {
    if (value) {
      navigator.clipboard.writeText(value);
      setTooltip('Texte copié !');
      setTimeout(() => {
        setTooltip(null);
      }, 2000);
    }
  };

  if (!retraction) {
    return null;
  }

  const nameToDisplaySAV =
    retraction?.authorSAV?.firstName +
    ' ' +
    retraction?.authorSAV?.lastName +
    ' (SAV)';

  const nameToDisplaySLO =
    retraction?.authorSLO?.firstName +
    ' ' +
    retraction?.authorSLO?.lastName +
    ' (SLO)';

  // console.log(retraction);

  return (
    <>
      <Paper
        elevation={16}
        key={retraction?._id}
        sx={{
          padding: 0,
          marginBottom: 1,
          boxShadow: 'none',
        }}
      >
        <Card
          elevation={5}
          sx={{
            display: 'flex',
            gap: 2,
            padding: 1,
            alignItems: 'center',
            justifyContent: 'space-evenly',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <ActionOnCommandIcon fontSize="large" />
              <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
                {nameToDisplaySAV + ' / ' + nameToDisplaySLO}
              </Typography>
              <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
                {`Rétractation (${retraction?.reason?.name})`}
              </Typography>
              <Typography component={'span'} variant="caption">
                {new Date(retraction?.updatedAt).toLocaleString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </Typography>
            </Box>
            <Divider />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <OrderCancelIcon fontSize="medium" />
              <Chip
                label={`Rétractation de commande ${retraction?.status?.name}`}
                color={
                  retraction?.status?.code === 'CREATED' ||
                  retraction?.status?.code === 'PROCESSED'
                    ? 'success'
                    : retraction?.status?.code === 'ERRORED'
                      ? 'error'
                      : 'warning'
                }
                variant="outlined"
                size="small"
                sx={{ width: 'fit-content', borderRadius: '5px' }}
              />
            </Box>
            Référence de rétractation
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            >
              {retraction?.reference}
            </Typography>
          </Box>
          <Box
            sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              startIcon={<InfoOutlinedIcon />}
              variant="outlined"
              color="primary"
              sx={{ fontSize: '12px' }}
              onClick={() => setIsModalOpen(true)}
            >
              Afficher les informations
            </Button>
          </Box>
          {retraction?.refund && (
            <Box sx={{ width: '100%' }}>
              <Divider>Remboursement</Divider>
              <Box sx={{ width: '95%', ml: '5%' }}>
                <RefundRecap
                  refund={{
                    ...retraction?.refund,
                    refundType: { name: 'Rétractation' },
                    orderRef: retraction?.order?.reference,
                  }}
                  currency={currency}
                />
              </Box>
            </Box>
          )}
        </Card>
      </Paper>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Détails de la rétractation</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            <Typography>
              <strong>Référence:</strong> {retraction?.reference}
            </Typography>
            <Typography>
              <strong>Commande:</strong> {retraction?.order?.reference}
            </Typography>

            <Typography>
              <strong>Type:</strong>{' '}
              {retraction?.type === 'total' ? 'Totale' : 'Partielle'}
            </Typography>

            <Typography>
              <strong>Motif:</strong> {retraction?.reason?.name}
            </Typography>

            <Typography>
              <strong>Statut:</strong> {retraction?.status?.name}
            </Typography>

            <Typography>
              <strong>Frais de retour:</strong> {retraction?.returnFees?.amount}
              {getSymbolFromCurrency(currency)}
            </Typography>

            {retraction?.packages?.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  p: 0,
                  m: 0,
                }}
              >
                {retraction?.packages?.map((p, index) => (
                  <Typography key={index} sx={{ fontSize: 12 }}>
                    {p.reference}
                  </Typography>
                ))}
              </Box>
            )}

            <Typography>
              <strong>Créé par:</strong>{' '}
              {nameToDisplaySAV + ' / ' + nameToDisplaySLO}
            </Typography>

            <Typography>
              <strong>Date de création:</strong>{' '}
              {new Date(retraction?.createdAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Typography>

            <Typography>
              <strong>Nombre de colis:</strong> {retraction?.packages?.length}
            </Typography>
            {retraction?.commentSAV && (
              <Typography>
                <strong>Commentaire SAV:</strong> {retraction?.commentSAV}
              </Typography>
            )}

            {retraction?.commentSLO && (
              <Typography>
                <strong>Commentaire SLO:</strong> {retraction?.commentSLO}
              </Typography>
            )}

            {retraction?.pickUps?.length > 0 && (
              <>
                <Divider>Informations d&apos;enlèvement</Divider>
                <Typography>
                  <strong>Adresse:</strong>{' '}
                  {retraction?.pickUps[0]?.location?.address1}
                  {retraction?.pickUps[0]?.location?.address2 && (
                    <>, {retraction?.pickUps[0]?.location?.address2}</>
                  )}
                </Typography>
                <Typography>
                  <strong>Code postal:</strong>{' '}
                  {retraction?.pickUps[0]?.location?.postalCode}
                </Typography>
                <Typography>
                  <strong>Ville:</strong>{' '}
                  {retraction?.pickUps[0]?.location?.city}
                </Typography>
                <Typography>
                  <strong>Pays:</strong>{' '}
                  {retraction?.pickUps[0]?.location?.country}
                </Typography>
                <Typography>
                  <strong>Téléphone:</strong>{' '}
                  {retraction?.pickUps[0]?.location?.phone}
                </Typography>
                {retraction?.pickUps[0]?.location?.additionalInfos && (
                  <Typography>
                    <strong>Informations complémentaires:</strong>{' '}
                    {retraction?.pickUps[0]?.location?.additionalInfos ||
                      'Non renseigné'}
                  </Typography>
                )}
                <Typography>
                  <strong>Transporteur:</strong>{' '}
                  {retraction?.pickUps[0]?.carrier?.name || 'Non renseigné'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>
                    <strong>Numéro de suivi : </strong>
                  </Typography>
                  <Tooltip
                    title={tooltip || 'Cliquez pour copier le numéro de suivi'}
                    arrow
                    placement="right"
                  >
                    <Typography
                      onClick={() =>
                        handleCopy(retraction?.pickUps?.[0]?.trackingNumber)
                      }
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'underline',
                        ml: 1,
                      }}
                    >
                      {retraction?.pickUps?.[0]?.trackingNumber ||
                        'Non Renseigné'}
                    </Typography>
                  </Tooltip>
                </Box>
              </>
            )}

            <Divider>Informations d&apos;enlèvement</Divider>

            {retraction?.refund && (
              <>
                <Divider>Remboursement</Divider>
                <RefundRecap
                  refund={{
                    ...retraction?.refund,
                    refundType: { name: 'Rétractation' },
                    orderRef: retraction?.order?.reference,
                  }}
                  currency={currency}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderRetractRecap;
